import { useMemo } from 'react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Project, DashboardProject } from '../../project/types/project';
import { useProjectFilters } from './useProjectFilters';
import type { ProjectFilters, RootState } from '../../../redux/types';
import {
  fetchProjectList, fetchProjectListSuccess, fetchProjectListFailure,
} from '../../../redux/Project/action';
import { useUserPermissionsAfterLogin } from '../../admin/hooks/useUserPermissionsAfterLogin';

interface UseHomePageStateProps {
  onProjectSelect: (project: DashboardProject) => void;
}

export const useHomePageState = ({ onProjectSelect }: UseHomePageStateProps) => {
  // // Sample project data - In real app, this would come from an API
  // const projects = useMemo<Project[]>(() => Array.from({ length: 12 }, (_, i) => ({
  //   id: `project-${i + 1}`,
  //   name: `Project ${i + 1}`,
  //   number: `PRJ-${(i + 1).toString().padStart(3, '0')}`,
  //   customer: `Customer ${(i % 3) + 1}`,
  //   startDate: '2024-01-01',
  //   endDate: '2024-12-31',
  //   type: (i % 3 === 0 ? 'Pipeline' : i % 3 === 1 ? 'Fabrication' : 'Others') as Project['type'],
  //   status: (i % 3 === 0 ? 'In Progress' : i % 3 === 1 ? 'Completed' : 'On Hold') as Project['status'],
  //   projectTypes: {
  //     pipeline: i===2 || i % 3 === 0,  //if i is 2 or 0, then it is pipeline
  //     fabrication: i===2 || i % 3 === 1, //if i is 2 or 1, then it is fabrication
  //     other: i % 3 === 2
  //   },
  //   fabricationOptions: {
  //     buckleArrestor:  i % 2 === 0,
  //     pipeInPipe:  i % 2 === 1,
  //     bulkhead: i===2 || i % 3 === 0
  //   },
  //   features: {
  //     welding: true,
  //     coating: i % 2 === 0,
  //     assembly: true,
  //     testing: i % 2 === 1,
  //     inspection: true
  //   },
  //   projectScope: {
  //     engineering: true,
  //     procurement: i % 2 === 0,
  //     construction: true,
  //     commissioning: i % 2 === 1
  //   },
  //   location: `Location ${(i % 4) + 1}`
  // })), []);


  const { userRole, canAccessAdminFeatures, userPermissions } = useUserPermissionsAfterLogin();
  
  // Get user_id from userPermissions data
  const userId = userPermissions?.user_id || null;


  const dispatch = useDispatch();
  const projectState = useSelector((state: RootState) => state.project);
  
  // Actions
  const loadProjects = useCallback((filters: ProjectFilters = {}) => {
    // Super admin and admin can fetch all projects (no userId filter)
    // User and viewer should fetch projects filtered by their userId
    if (canAccessAdminFeatures()) {
      // Don't send userId for super_admin and admin
      dispatch(fetchProjectList({...filters}));
    } else {
      // Send userId for user and viewer roles
      dispatch(fetchProjectList({...filters, userId: userId}));
    }
  }, [dispatch, canAccessAdminFeatures, userId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]); 

  const { isProjectUpdateLoading, isProjectListLoading } = projectState;
  const projects = (projectState as any).ProjectList || [];
  const {
    filteredProjects,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    resetFilters,
    uniqueCustomers,
    hasActiveFilters
  } = useProjectFilters({ projects });
  const handleProjectClick = (project: Project) => {
    const dashboardProject: DashboardProject = {
      project: project,
      team: [], // Initialize with empty array if not available
      documents: [], // Initialize with empty array if not available
      materials: [] // Initialize with empty array if not available
    };
    
    onProjectSelect(dashboardProject);
  };

  const mapProjectStatus = (status: Project['progress']): DashboardProject['project']['progress'] => {
    switch (status) {
      case 'In Progress':
        return 'active';
      case 'Completed':
        return 'completed';
      case 'On Hold':
        return 'on-hold';
      default:
        return 'active';
    }
  };

  return {
    isProjectUpdateLoading,
    isProjectListLoading,
    projects,
    filteredProjects,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    resetFilters,
    uniqueCustomers,
    handleProjectClick,
    hasActiveFilters,
    loadProjects
  };
}; 